import { useEffect } from 'react'
import { QueryProvider } from './providers/QueryProvider'
import { ThemeProvider } from './providers/ThemeProvider'
import { RTLProvider } from './providers/RTLProvider'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { TagsProvider } from './contexts/TagsContext'
import { AppRouter } from './router'
import { UpdateAvailableSnackbar } from './components/common/UpdateAvailableSnackbar'
import { setLogoutHandler, setNdaRequiredHandler } from './api/client'
import { useAuthStore } from './store/authStore'
import { useNdaStore } from './store/ndaStore'
import { initializeCache } from './utils/adventureCache'

function App() {
  const { logout, isAuthenticated } = useAuthStore()

  // Set up the logout handler for the API client
  useEffect(() => {
    setLogoutHandler(logout)
  }, [logout])

  // Set up the NDA handler for the API client. A 403 nda_required on any request raises the
  // flag the gate reads, so a version published mid-session shows the agreement instead of
  // surfacing as an unexplained failure.
  useEffect(() => {
    setNdaRequiredHandler(version => {
      useNdaStore.getState().setBlocked(version)
    })
  }, [])

  // Whose NDA state it is matters. Signing out drops the flag so the next person to sign in is
  // judged by the server's answer about them, not by a refusal aimed at the previous account.
  useEffect(() => {
    if (!isAuthenticated) {
      useNdaStore.getState().clearBlocked()
    }
  }, [isAuthenticated])

  // Initialize IndexedDB cache on app startup
  useEffect(() => {
    initializeCache().catch((error) => {
      console.warn('Failed to initialize cache:', error)
    })
  }, [])

  return (
    <ErrorBoundary>
      <QueryProvider>
        <ThemeProvider>
          <RTLProvider>
            <TagsProvider>
              <AppRouter />
              <UpdateAvailableSnackbar />
            </TagsProvider>
          </RTLProvider>
        </ThemeProvider>
      </QueryProvider>
    </ErrorBoundary>
  )
}

export default App
